import {
  User, Heart, Phone, Briefcase, CreditCard, DollarSign,
  PlusCircle, Calendar, CalendarCheck, Receipt, MinusCircle,
  Banknote, FileCheck,
} from 'lucide-react';
import type { Country, Employee, Field, Section, SectionStatus, Values } from './types';

export function authHeaders(): Record<string, string> {
  const t = localStorage.getItem('access_token') || localStorage.getItem('token');
  return t ? { Authorization: 'Bearer ' + t } : {};
}

export function isFilled(v: unknown): boolean {
  return v !== undefined && v !== null && String(v).trim() !== '';
}

export function taxIntroFor(country: Country): string {
  const c = country.code;
  if (c === 'CA') return "Enter the employee's tax setup once (from their TD1). Novala calculates CRA federal tax, provincial tax, CPP, EI, employer contributions, and net pay automatically at every pay run.";
  if (c === 'US') return "Enter the employee's tax setup once (from their W-4). Novala calculates federal income tax, FICA (Social Security and Medicare), state tax, employer contributions, and net pay automatically at every pay run.";
  if (c === 'GB') return "Enter the employee's tax setup once (from their P45, P46, or starter checklist). Novala calculates PAYE income tax, employee and employer National Insurance, student loan deductions, and net pay automatically at every pay run.";
  if (c === 'AU') return "Enter the employee's tax setup once (from their TFN declaration). Novala calculates PAYG withholding, Medicare levy, HELP/HECS, employer superannuation, and net pay automatically at every pay run.";
  return "Enter the employee's tax setup once. Novala calculates payroll taxes and net pay automatically at every pay run.";
}

export function buildSections(country: Country): Section[] {
  return [
    { id: 'personal', title: 'Personal information', icon: User, isCustomSection: true, required: true, fields: [], intro: 'Name, contact, address, and identification for this employee.' },
    { id: 'emergency', title: 'Emergency contacts', icon: Phone, isCustomSection: true, required: false, fields: [], intro: 'People to contact in case of emergency.' },
    { id: 'employment', title: 'Employment details', icon: Briefcase, isCustomSection: true, required: true, fields: [], intro: 'Role, dates, work location, and tenure.' },
    { id: 'payment', title: 'Payment method', icon: CreditCard, isCustomSection: true, required: true, fields: [], intro: 'How this employee receives their pay.' },
    { id: 'basepay', title: 'Base pay', icon: DollarSign, required: true, isCustomSection: true, fields: [] },
    { id: 'addpay', title: 'Additional pay types', icon: Banknote, required: false, fields: [], intro: 'Bonus, overtime, stat pay, and other earnings for this employee.' },
    { id: 'statholiday', title: 'Stat holiday pay', icon: CalendarCheck, required: false, fields: [], intro: 'Regular workdays and eligibility for statutory holiday pay.' },
    { id: 'timeoff', title: 'Time off', icon: Calendar, isCustomSection: true, required: false, fields: [], intro: 'Vacation, sick pay, and unpaid leave policies for this employee.' },
    { id: 'deductions', title: 'Deductions and contributions', icon: MinusCircle, isCustomSection: true, required: false, fields: [], intro: 'Voluntary items like RRSP, health insurance, or garnishments.' },
    { id: 'tax', title: 'Tax setup (' + country.taxForm + ')', icon: FileCheck, required: true, fields: country.taxFields, intro: taxIntroFor(country) },
  ];
}

export function sectionStatus(section: Section, values: Values): SectionStatus {
  const reqKeys = section.fields.filter((f) => f.req).map((f) => f.k);
  const anyFilled = section.fields.some((f) => isFilled(values[f.k]));
  if (reqKeys.length > 0) {
    const allReq = reqKeys.every((k) => isFilled(values[k]));
    if (allReq) return 'done';
    return anyFilled ? 'edit' : 'start';
  }
  return anyFilled ? 'done' : 'edit';
}

export function formatDateForInput(d: string | null | undefined): string {
  if (!d) return '';
  if (typeof d === 'string' && d.length >= 10) return d.slice(0, 10);
  return d;
}

export function employeeToValues(emp: Employee | null | undefined, country: Country): Values {
  if (!emp) return {};
  const ti = emp.tax_info || {};
  const ded = Array.isArray(emp.deductions_list) && emp.deductions_list[0] ? emp.deductions_list[0] as Record<string, unknown> : {};
  const empTypeMap: Record<string, string> = { full_time: 'Full-time', part_time: 'Part-time', casual: 'Casual' };
  const payScheduleMap: Record<string, string> = { weekly: 'Weekly', bi_weekly: 'Bi-weekly', semi_monthly: 'Semi-monthly', monthly: 'Monthly' };
  const rate = emp.pay_type === 'hourly' ? emp.hourly_rate : emp.salary_amount;
  const result: Values = {
    tax_info: emp.tax_info || {},
    name: [emp.first_name, emp.last_name].filter(Boolean).join(' '),
    preferred: emp.preferred_name || '',
    email: emp.personal_email || '',
    mobilePhone: emp.phone || '',
    street: emp.address_line1 || '',
    city: emp.city || '',
    province: emp.province_or_state || '',
    postal: emp.postal_or_zip || '',
    birth: formatDateForInput(emp.date_of_birth),
    gender: emp.gender || '',
    taxId: emp.sin_or_ssn || '',
    ecName: emp.emergency_contact_name || '',
    ecRel: emp.emergency_contact_relationship || '',
    ecPhone: emp.emergency_contact_phone || '',
    ecEmail: emp.emergency_contact_email || '',
    empId: (emp as any).employee_number || '',
    jobTitle: (emp as any).position_title || '',
    empType: empTypeMap[(emp as any).employment_type] || (emp as any).employment_type || '',
    startDate: formatDateForInput((emp as any).start_date),
    workLocationId: (emp as any).work_location_id || '',
    department: (emp as any).department || '',
    manager: (emp as any).manager_name || '',
    method: (emp as any).account_type === 'direct_deposit' ? 'Direct deposit' : ((emp as any).account_type === 'cheque' ? 'Cheque' : ''),
    institution: (emp as any).bank_name || '',
    transit: (emp as any).transit_number || '',
    account: (emp as any).account_number_encrypted || '',
    payType: emp.pay_type === 'hourly' ? 'Hourly' : (emp.pay_type === 'salary' ? 'Salary' : ''),
    rate: rate != null ? String(rate) : '',
    standardHours: emp.hours_per_week != null ? String(emp.hours_per_week) : '',
    paySchedule: payScheduleMap[emp.pay_schedule || ''] || emp.pay_schedule || '',
    overtime: (ti as any).overtime || '',
    bonus: (ti as any).bonus || '',
    vacationPay: (ti as any).vacation_pay_enabled || '',
    vacationPolicy: emp.vacation_policy || '',
    accrualRate: (ti as any).accrual_rate || '',
    balanceHours: (ti as any).balance_hours != null ? String((ti as any).balance_hours) : '',
    deductionName: (ded as any).name || '',
    deductionAmount: (ded as any).amount != null ? String((ded as any).amount) : '',
  };
  country.taxFields.forEach((f) => {
    result[f.k] = (ti as any)[f.k] != null ? String((ti as any)[f.k]) : '';
  });
  return result;
}

export function valuesToPatch(sectionId: string, v: Values, currentEmp: Employee | null | undefined, country: Country): Record<string, unknown> {
  const ti = (currentEmp && currentEmp.tax_info) || {};
  if (sectionId === 'personal') {
    const parts = (v.name || '').trim().split(/\s+/);
    return {
      first_name: parts[0] || '',
      last_name: parts.slice(1).join(' ') || '',
      preferred_name: v.preferred || null,
      personal_email: v.email || '',
      phone: v.mobilePhone || null,
      address_line1: v.street || null,
      city: v.city || null,
      province_or_state: v.province || null,
      postal_or_zip: v.postal || null,
      date_of_birth: v.birth || null,
      gender: v.gender || null,
      sin_or_ssn: v.taxId || null,
    };
  }
  if (sectionId === 'emergency') {
    return {
      emergency_contact_name: v.ecName || null,
      emergency_contact_relationship: v.ecRel || null,
      emergency_contact_phone: v.ecPhone || null,
      emergency_contact_email: v.ecEmail || null,
    };
  }
  if (sectionId === 'employment') {
    const empTypeMap: Record<string, string> = { 'Full-time': 'full_time', 'Part-time': 'part_time', 'Casual': 'casual' };
    return {
      employee_number: v.empId || null,
      position_title: v.jobTitle || null,
      employment_type: empTypeMap[v.empType] || (v.empType || 'full_time').toLowerCase().replace(/[- ]/g, '_'),
      start_date: v.startDate || null,
      work_location_id: v.workLocationId || null,
      department: v.department || null,
      manager_name: v.manager || null,
    };
  }
  if (sectionId === 'payment') {
    const dd = v.method === 'Direct deposit';
    return {
      bank_name: dd ? (v.institution || null) : null,
      transit_number: dd ? (v.transit || null) : null,
      account_number_encrypted: dd ? (v.account || null) : null,
      account_type: dd ? 'direct_deposit' : 'cheque',
    };
  }
  if (sectionId === 'basepay') {
    const isHourly = v.payType === 'Hourly';
    const rateNum = v.rate ? Number(v.rate) : null;
    const psMap: Record<string, string> = { Weekly: 'weekly', 'Bi-weekly': 'bi_weekly', 'Semi-monthly': 'semi_monthly', Monthly: 'monthly' };
    return {
      pay_type: isHourly ? 'hourly' : 'salary',
      hourly_rate: isHourly ? rateNum : null,
      salary_amount: isHourly ? null : rateNum,
      hours_per_week: v.standardHours ? Number(v.standardHours) : null,
      pay_schedule: psMap[v.paySchedule] || (v.paySchedule || '').toLowerCase().replace(/[- ]/g, '_'),
    };
  }
  if (sectionId === 'addpay') {
    return { tax_info: { ...ti, overtime: v.overtime || null, bonus: v.bonus || null, vacation_pay_enabled: v.vacationPay || null } };
  }
  if (sectionId === 'timeoff') {
    return {
      vacation_policy: v.vacationPolicy || null,
      tax_info: { ...ti, accrual_rate: v.accrualRate || null, balance_hours: v.balanceHours ? Number(v.balanceHours) : null },
    };
  }
  if (sectionId === 'tax') {
    const newTi: Record<string, unknown> = { ...ti };
    country.taxFields.forEach((f) => {
      if (f.t === 'number' || f.t === 'money') {
        newTi[f.k] = isFilled(v[f.k]) ? Number(v[f.k]) : null;
      } else {
        newTi[f.k] = v[f.k] || null;
      }
    });
    return { tax_info: newTi };
  }
  if (sectionId === 'deductions') {
    return {
      deductions_list: v.deductionName ? [{ name: v.deductionName, amount: v.deductionAmount ? Number(v.deductionAmount) : 0 }] : [],
    };
  }
  return {};
}

export function initialsFrom(name: string | null | undefined): string {
  if (!name) return '?';
  const parts = String(name).trim().split(/\s+/);
  const f = (parts[0] && parts[0][0]) || '';
  const l = parts.length > 1 ? parts[parts.length - 1][0] : '';
  return (f + l).toUpperCase();
}

export function formatMoney(v: number | string | null | undefined): string | null {
  if (!isFilled(v)) return null;
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);
  return '$' + n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function formatViewValue(field: Field, value: unknown): string | null {
  if (field.t === 'wloc-select') {
    if (!value) return '';
    const locs = field.workLocations || [];
    if (locs.length === 0) return 'Loading location...';
    const found = locs.find((l) => String(l.id) === String(value));
    return found ? (found.name || '(Unnamed)') : '';
  }
  if (!isFilled(value)) return null;
  if (field.t === 'money') return formatMoney(value as any);
  return String(value);
}